import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  AccountBalance as CreditsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { creditApi, CreditHistoryItem, OrganizationCredits, MonthlyCreditHistoryGrouped } from '../utils/creditApi';
import { extraCreditsApi } from '../utils/extraCreditsApi';
import { useTranslation } from 'react-i18next';

const CreditHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { user, currentOrganization } = useAuth();
  const [creditHistory, setCreditHistory] = useState<CreditHistoryItem[]>([]);
  const [monthlyCreditHistory, setMonthlyCreditHistory] = useState<MonthlyCreditHistoryGrouped[]>([]);
  const [organizationCredits, setOrganizationCredits] = useState<OrganizationCredits | null>(null);
  const [organizationExtraCredits, setOrganizationExtraCredits] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCreditData();
  }, []);

  const fetchCreditData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar dados da organização e histórico de créditos em paralelo
      const [organizationCreditsData, creditHistoryData, monthlyCreditHistoryData, extraCreditsData] = await Promise.all([
        creditApi.getOrganizationCredits(),
        creditApi.getCreditHistory(),
        creditApi.getMonthlyCreditHistoryGrouped(),
        extraCreditsApi.getOrganizationExtraCredits()
      ]);

      setOrganizationCredits(organizationCreditsData);
      setCreditHistory(creditHistoryData);
      setMonthlyCreditHistory(monthlyCreditHistoryData);
      setOrganizationExtraCredits(extraCreditsData);
    } catch (err) {
      setError(t('pages.credits.loadingError'));
      console.error('Error fetching credit data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/admin');
  };

  const handleRefresh = () => {
    fetchCreditData();
  };

  const formatMonth = (monthString: string) => {
    const date = new Date(monthString);
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const getUsagePercentage = () => {
    if (!organizationCredits) return 0;
    const totalAvailable = organizationCredits.totalAvailableCredits;
    return totalAvailable > 0 ? (organizationCredits.usedThisMonth / totalAvailable) * 100 : 0;
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return theme.palette.error.main;
    if (percentage >= 70) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getAgentUsageStatus = (usedCredits: number) => {
    if (!organizationCredits) return { label: 'N/A', color: 'default' as const, percentage: 0, tooltip: t('pages.credits.notAvailable') };

    const totalCredits = organizationCredits.credit;
    const usagePercentage = (usedCredits / totalCredits) * 100;

    if (usagePercentage >= 15) {
      return {
        label: t('pages.credits.high'),
        color: 'error' as const,
        percentage: usagePercentage,
        tooltip: `${usedCredits} ${t('pages.credits.credits')} (${usagePercentage.toFixed(1)}${t('pages.credits.percentage')})`
      };
    } else if (usagePercentage >= 8) {
      return {
        label: t('pages.credits.medium'),
        color: 'warning' as const,
        percentage: usagePercentage,
        tooltip: `${usedCredits} ${t('pages.credits.credits')} (${usagePercentage.toFixed(1)}${t('pages.credits.percentage')})`
      };
    } else {
      return {
        label: t('pages.credits.low'),
        color: 'success' as const,
        percentage: usagePercentage,
        tooltip: `${usedCredits} ${t('pages.credits.credits')} (${usagePercentage.toFixed(1)}${t('pages.credits.percentage')})`
      };
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Fallback para debug
  if (!loading && monthlyCreditHistory.length === 0 && !error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{ mr: 2, textTransform: 'none' }}
          >
            {t('pages.credits.back')}
          </Button>
          <HistoryIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            {t('pages.credits.title')}
          </Typography>
        </Box>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            {t('pages.credits.noData')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pages.credits.noDataDescription')}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mr: 2, textTransform: 'none' }}
        >
          {t('pages.credits.back')}
        </Button>
        <HistoryIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          {t('pages.credits.title')}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={t('pages.credits.refresh')}>
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Resumo atual */}
      {organizationCredits && (
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          mb: 4
        }}>
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CreditsIcon sx={{ fontSize: 24, mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t('pages.credits.currentCredits')}
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                  {organizationCredits.totalAvailableCredits - organizationCredits.usedThisMonth}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('pages.credits.ofAvailable', { total: organizationCredits.totalAvailableCredits })}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    • {t('pages.credits.monthly')}: {organizationCredits.credit}
                  </Typography>
                  {organizationCredits.extraCredits > 0 && (
                    <Typography variant="body2" color="success.main">
                      • {t('pages.credits.extras')}: {organizationCredits.extraCredits}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 24, mr: 1, color: 'secondary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t('pages.credits.usedThisMonth')}
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'secondary.main', mb: 1 }}>
                  {organizationCredits.usedThisMonth}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={getUsagePercentage()}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: alpha(theme.palette.grey[300], 0.3),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getUsageColor(getUsagePercentage()),
                        borderRadius: 4,
                      }
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('pages.credits.percentageUsed', { percentage: getUsagePercentage().toFixed(1) })}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Tabela de histórico */}
      <Paper elevation={2}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('pages.credits.monthlyHistory')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pages.credits.monthlyHistoryDescription')}
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('pages.credits.month')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('pages.credits.agent')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('pages.credits.creditsUsed')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('common.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthlyCreditHistory.map(monthData => (
                <React.Fragment key={monthData.month}>
                  {monthData.agentDetails.map((agent, index) => (
                    <TableRow key={`${monthData.month}-${agent.id}`} hover>
                      <TableCell>
                        {index === 0 && (
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {monthData.monthDisplay}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.primary.main,
                              mr: 1
                            }}
                          />
                          {agent.agentName}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {agent.used}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const status = getAgentUsageStatus(agent.used);
                          return (
                            <Tooltip title={status.tooltip} arrow>
                              <Chip
                                label={status.label}
                                size="small"
                                color={status.color}
                                variant="outlined"
                              />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Linha de total do mês */}
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell colSpan={2}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Total do mês
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {monthData.totalUsed}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                        label="Total"
                        size="small"
                        color="primary"
                        variant="filled"
                      />
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {monthlyCreditHistory.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Nenhum histórico encontrado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Os dados de uso de créditos aparecerão aqui conforme forem utilizados
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Seção de Créditos Extras */}
      {organizationExtraCredits && organizationExtraCredits.extraCreditsHistory.length > 0 && (
        <Paper sx={{ mt: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CreditsIcon sx={{ fontSize: 24, mr: 1, color: 'success.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('pages.credits.extraCredits')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {t('pages.credits.extraCreditsDescription')}
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('pages.credits.quantity')}</TableCell>
                  <TableCell>{t('pages.credits.used')}</TableCell>
                  <TableCell>{t('pages.credits.available')}</TableCell>
                  <TableCell>{t('pages.credits.reason')}</TableCell>
                  <TableCell>{t('pages.credits.addedAt')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizationExtraCredits.extraCreditsHistory.map((credit: any) => {
                  const used = credit.used || 0;
                  const available = Math.max(0, credit.amount - used);
                  const isFullyUsed = used >= credit.amount;
                  
                  return (
                    <TableRow key={credit.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          +{credit.amount.toLocaleString('pt-BR')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={isFullyUsed ? 'error.main' : 'text.secondary'}>
                          {used.toLocaleString('pt-BR')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color={available > 0 ? 'success.main' : 'text.secondary'}>
                          {available.toLocaleString('pt-BR')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {credit.reason}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(credit.addedAt).toLocaleDateString('pt-BR')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          sx={{
                            color: theme.palette.mode === 'dark' ? '#000' : undefined
                          }}
                          label={isFullyUsed ? 'Esgotado' : credit.isActive ? 'Em Uso' : 'Desativado'}
                          color={isFullyUsed ? 'error' : credit.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default CreditHistoryPage;
